/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  notification,
  InputNumber,
  Radio,
  Table,
} from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import {
  Plus,
  X,
  Circle,
  CircleDot,
  Square,
  CheckSquare,
  Image,
  Timer,
} from 'lucide-react';
import '../ShippingManagement/ShipmentDashBoardPage.css';
import PqtFormSelect from './PqtFormSelect';

const { TextArea } = Input;

/** Comparator option list — must match API / Excel values for controlled Select. */
export const PQT_COMPARATOR_OPTIONS = ['<', '<=', '>', '>=', '=', 'BETWEEN', 'None'];

const COMPARATOR_ALIASES = {
  LT: '<',
  LTE: '<=',
  GT: '>',
  GTE: '>=',
  EQ: '=',
};

/**
 * Map backend values onto PQT_COMPARATOR_OPTIONS so the Comparator select shows a label and options stay usable.
 */
export function normalizePqtComparator(raw) {
  if (raw == null) return '<';
  const v = String(raw).trim();
  if (v === '') return '<';
  if (PQT_COMPARATOR_OPTIONS.includes(v)) return v;
  const alias = COMPARATOR_ALIASES[v.toUpperCase()];
  if (alias) return alias;
  const up = v.toUpperCase();
  if (up === 'BETWEEN') return 'BETWEEN';
  if (up === 'NONE') return 'None';
  return '<';
}

function pqtBoolMandatory(value) {
  if (value === false || value === 'false' || value === 'False') return false;
  return true;
}

/** Mandatory / Optional — Lucide icons, value is boolean (Form getValueFromEvent). */
function PqtModalMandatoryRow({ value, onChange }) {
  const isMandatory = pqtBoolMandatory(value);
  return (
    <div className="pqt-modal-option-row">
      <div className="pqt-lucide-segment" role="radiogroup" aria-label="Test required for all welds">
        <button
          type="button"
          className={`pqt-lucide-segment__btn${isMandatory ? ' pqt-lucide-segment__btn--active' : ''}`}
          role="radio"
          aria-checked={isMandatory}
          onClick={() => onChange(true)}
        >
          {isMandatory ? (
            <CircleDot size={20} strokeWidth={2} className="pqt-lucide-accent" aria-hidden />
          ) : (
            <Circle size={20} strokeWidth={2} className="pqt-lucide-muted" aria-hidden />
          )}
          <span>Mandatory</span>
        </button>
        <button
          type="button"
          className={`pqt-lucide-segment__btn${!isMandatory ? ' pqt-lucide-segment__btn--active' : ''}`}
          role="radio"
          aria-checked={!isMandatory}
          onClick={() => onChange(false)}
        >
          {!isMandatory ? (
            <CircleDot size={20} strokeWidth={2} className="pqt-lucide-accent" aria-hidden />
          ) : (
            <Circle size={20} strokeWidth={2} className="pqt-lucide-muted" aria-hidden />
          )}
          <span>Optional</span>
        </button>
      </div>
      <p className="pqt-modal-option-hint">
        {isMandatory
          ? '* Test required for all the welds.'
          : '* Test is not required for all the welds.'}
      </p>
    </div>
  );
}

/** Single boolean toggle with Lucide square + row icon (Form getValueFromEvent). */
function PqtModalToggleRow({ value, onChange, label, RowIcon, syncParent, hintTrue, hintFalse }) {
  const active = value === true || value === 'true';
  const toggle = () => {
    const next = !active;
    onChange(next);
    if (typeof syncParent === 'function') syncParent(next);
  };
  return (
    <div className="pqt-modal-option-row">
      <button
        type="button"
        className="pqt-lucide-check-trigger"
        onClick={toggle}
        aria-pressed={active}
        aria-label={label}
      >
        {active ? (
          <CheckSquare size={22} strokeWidth={2} className="pqt-lucide-accent" aria-hidden />
        ) : (
          <Square size={22} strokeWidth={2} className="pqt-lucide-muted" aria-hidden />
        )}
        {RowIcon ? <RowIcon size={18} strokeWidth={2} className="pqt-lucide-label-icon" aria-hidden /> : null}
        <span>{label}</span>
      </button>
      <p className="pqt-modal-option-hint">{active ? hintTrue : hintFalse}</p>
    </div>
  );
}

const AddNewPQTTest = (props) => {
  const {
    isTestModalVisible,
    setIsTestModalVisible,
    onPqtTestModalOpen,
    sowStationId,
    testTypesList,
    onSaveTestDetails,
    criteriaList,
    inputValue,
    setInputValue,
    valueObj,
    setValueObj,
    testTypeValue,
    setTestTypeValue,
    criteria,
    setCriteria,
    testValue,
    setTestValue,
    testForm,
    setEditTest,
    editTest,
    editTestId,
    // loadingBtn,
    // setLoadingBtn,
    onSaveEditTestDetails,
    imagesRequired,
    setImagesRequired,
    stopwatchValueRequired,
    setStopwatchValueRequired,
    comparator,
    setComparator,
    selectedSOW,
    selectedTestData,
    projectTeamMembersAccessList,
  } = props;
  // console.log("criteriaList", criteriaList)
  const comparatorValue = PQT_COMPARATOR_OPTIONS;

  const [noOfSample, setNoOfSample] = useState(1);
  const [testId, setTestId] = useState('');
  const [minValue, setMinValue] = useState('0');
  const [maxValue, setMaxValue] = useState('0');
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [isTableSelected, setIsTableSelected] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [colCount, setColCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [form] = Form.useForm();

  // Separate useState for each input field
  const [processDescription, setProcessDescription] = useState('');
  const [applicableStandardsCode, setApplicableStandardsCode] = useState('');
  const [testEquipment, setTestEquipment] = useState('');
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
  const [dataTable, setDataTable] = useState({
    rows: [], // Initially empty
    columns: [],
  });
  const [showExtraFields, setShowExtraFields] = useState(true); // Default to showing extra fields

  // Single (per table) mode: keep each row aligned with global unit / comparator / min / max
  // so `dataTable` matches what we submit and any consumer of row state stays current.
  useEffect(() => {
    if (!showExtraFields) return;
    setDataTable((prevData) => {
      if (!prevData.rows.length) return prevData;
      return {
        ...prevData,
        rows: prevData.rows.map((row) => ({
          ...row,
          unit: testValue || row.unit,
          comparator,
          minValue:
            minValue != null && minValue !== '' ? minValue : row.minValue ?? '',
          maxValue:
            maxValue != null && maxValue !== '' ? maxValue : row.maxValue ?? '',
        })),
      };
    });
  }, [testValue, comparator, minValue, maxValue, showExtraFields]);

  useEffect(() => {
    // console.log(editTest, inputValue, selectedTestData)
    if (editTest) {
      setProcessDescription(selectedTestData.processDescription);
      setApplicableStandardsCode(selectedTestData.applicableStandardsCode);
      setTestEquipment(selectedTestData.testEquipment);
      setAcceptanceCriteria(selectedTestData.acceptanceCriteria);

      // Set radio button based on mode field from API data
      let isSingleMode = true; // Default
      if (selectedTestData.tableData && selectedTestData.tableData.mode) {
        isSingleMode = selectedTestData.tableData.mode === 'single';
        setShowExtraFields(isSingleMode);
      } else if (selectedTestData?.tableData?.mode === 'multiple') {
        isSingleMode = false;
        setShowExtraFields(false);
      } else {
        // Default to Single (per table) if mode field doesn't exist
        isSingleMode = true;
        setShowExtraFields(true);
      }

      const mandatoryVal = pqtBoolMandatory(selectedTestData.mandatory);
      const imgReq = !!selectedTestData.imagesRequired;
      const swReq = !!selectedTestData.stopwatchValueRequired;
      setImagesRequired(imgReq);
      setStopwatchValueRequired(swReq);

      var editField = {
        mandatory: mandatoryVal,
        imagesRequired: imgReq,
        stopwatchValueRequired: swReq,
        processDescription: selectedTestData.processDescription,
        applicableStandardsCode: selectedTestData.applicableStandardsCode,
        testEquipment: selectedTestData.testEquipment,
        acceptanceCriteria: selectedTestData.acceptanceCriteria,
        extraFieldsMode: isSingleMode, // Set form field value based on showExtraFields
      };
      if (criteria === 'Table') {
        if (selectedTestData.tableData) {
          let parsedTableData = selectedTestData.tableData;

          // If tableData is a JSON string, parse it
          if (typeof selectedTestData.tableData === 'string') {
            try {
              parsedTableData = JSON.parse(selectedTestData.tableData);
            } catch (error) {
              console.error('Error parsing tableData JSON:', error);
              parsedTableData = null;
            }
          }

          if (parsedTableData) {
            // Ensure all rows have the new fields initialized
            const updatedTableData = {
              ...parsedTableData,
              rows: parsedTableData.rows.map(row => ({
                ...row,
                comparator: normalizePqtComparator(row.comparator),
                minValue: row.minValue || '',
                maxValue: row.maxValue || '',
              }))
            };
            setDataTable(updatedTableData);
            editField.colCount = updatedTableData.columns.length;
            editField.rowCount = updatedTableData.rows.length;
            setRowCount(updatedTableData.rows.length);
            setColCount(updatedTableData.columns.length);
          }
        }

        setInputValue(false);
        setIsTableSelected(true);
      } else if (criteria === 'Value') {
        setRowCount(0);
        setColCount(0);
        // setDataTable([])

        setIsTableSelected(false);
        editField.noOfSample = selectedTestData.noOfSample;
        setNoOfSample(selectedTestData.noOfSample);
      }
      //  onChangeCriteria(criteria)
      testForm.setFieldsValue(editField);
      setComparator(normalizePqtComparator(selectedTestData.comparator));
    }
  }, [isTestModalVisible]);

  const showModal = () => {
    onPqtTestModalOpen?.(sowStationId);
    setIsTestModalVisible(true);
    setInputValue(false);
    setEditTest(false);
    setShowExtraFields(true); // Reset to default
    setMinValue('0');
    setMaxValue('0');
    sessionStorage.setItem('sowStationId', sowStationId);
    var editField = {
      mandatory: true,
      imagesRequired: false,
      stopwatchValueRequired: false,
    };
    setProcessDescription('');
    setApplicableStandardsCode('');
    setTestEquipment('');
    setAcceptanceCriteria('');
    setImagesRequired(false);
    setStopwatchValueRequired(false);
    testForm.setFieldsValue(editField);

    //setting test mandatory by default
  };

  /** Function to reset fields on cancel */
  const handleCancel = () => {
    testForm.resetFields(); //resetting input fields
    setIsTestModalVisible(false);
    setInputValue(false);
    setValueObj('');
    setTestTypeValue('');
    setImagesRequired(false);
    setStopwatchValueRequired(false);
    setComparator('<');
    setColCount(0);
    setRowCount(0);
    setProcessDescription('');
    setApplicableStandardsCode('');
    setTestEquipment('');
    setAcceptanceCriteria('');
    setShowExtraFields(true); // Reset to default
    setMinValue('0');
    setMaxValue('0');
  };

  function validateComparators(data) {
    if (
      (comparator == '<' || comparator == '<=') &&
      (typeof data.maxValue === 'undefined' || data.maxValue <= 0)
    ) {
      notification.warn({ message: 'Max Value should be greater than 0' });
      return false;
    }
    if (
      (comparator == '>' || comparator == '>=') &&
      (typeof data.minValue === 'undefined' || data.minValue <= 0)
    ) {
      notification.warn({ message: 'Min Value should be greater than 0' });
      return false;
    }
    if (
      comparator == 'BETWEEN' &&
      (typeof data.minValue === 'undefined' || data.minValue < 0)
    ) {
      notification.warn({ message: 'Min Value should be greater than 0 or 0' });
      return false;
    }
    if (
      comparator == 'BETWEEN' &&
      (typeof data.minValue === 'undefined' ||
        typeof data.maxValue === 'undefined' ||
        data.minValue >= data.maxValue)
    ) {
      notification.warn({ message: 'Max Value should be greater than Min Value' });
      return false;
    }
    if (comparator == '=' && (typeof data.maxValue === 'undefined' || data.maxValue <= 0)) {
      notification.warn({ message: 'Value should be greater than 0' });
      return false;
    }
    return true; // All validations passed
  }

  /** Function to save (add or edit test) */
  const handleOk = () => {
    testForm
      .validateFields()
      .then(async (data) => {
        if (data.criteria !== undefined) {
          // Check if the required fields are empty
          if (!data.testId || !data.criteria) {
            notification.warning({
              message: 'Fill All the required fields to proceed!',
            });
            return;
          }

          var sowStatId = sessionStorage.getItem('sowStationId');
          const resolvedImages =
            typeof data.imagesRequired === 'boolean' ? data.imagesRequired : !!imagesRequired;
          const resolvedStopwatch =
            typeof data.stopwatchValueRequired === 'boolean'
              ? data.stopwatchValueRequired
              : !!stopwatchValueRequired;

          if (data.criteria === 'Accept/Reject/NA' || data.criteria === 'Text') {
            const obj = {
              processDescription: processDescription,
              applicableStandardsCode: applicableStandardsCode,
              testEquipment: testEquipment,
              acceptanceCriteria: acceptanceCriteria,
              testId: data.testId,
              noOfSample: null,
              tableData: null,
              // typeOfTest: 'Accept/Reject/NA',
              typeOfTest: data.criteria,
              sowStationId: parseInt(sowStatId),
              testValueTypeId: testTypeValue?.id || null,
              mandatory: data.mandatory,
              imagesRequired: resolvedImages,
              stopwatchValueRequired: resolvedStopwatch,
            };
            // if (obj.typeOfTest === 'Text') {
            //   obj.testValueTypeId = 1;
            // }
            setLoadingBtn(true);
            if (editTest) {
              await onSaveEditTestDetails(obj, editTestId);
            } else {
              await onSaveTestDetails(obj);
            }

            setLoadingBtn(false);
            setIsTestModalVisible(false);
            setCriteria('');
            setInputValue(false);
            setTestValue('');
            setImagesRequired(false);
            setStopwatchValueRequired(false);
            setComparator('<');
            testForm.resetFields(); //resetting input fields
          } else if (data.criteria === 'Table') {
            // For Single (per table) mode, check global unit and comparator
            if (showExtraFields) {
              if (!data.testValue) {
                notification.warning({
                  message: 'Select Unit to Proceed!',
                });
                return;
              }
              if (!validateComparators(data)) {
                return; // Stop submission if validation fails
              }

            } else {
              // For Multiple (per row) mode, check each row has unit and comparator
              const invalidRows = dataTable.rows.filter(row =>
                !row.unit || !row.comparator
              );
              if (invalidRows.length > 0) {
                notification.warning({
                  message: 'Please select Unit and Comparator for all rows!',
                });
                return;
              }
              // Validate min/max values for each row
              for (const row of dataTable.rows) {
                if (row.comparator !== 'None') {
                  if (
                    (row.comparator === '<' || row.comparator === '<=') &&
                    (!row.maxValue || row.maxValue <= 0)
                  ) {
                    notification.warn({ message: `Max Value should be greater than 0 for ${row.name}` });
                    return;
                  }
                  if (
                    (row.comparator === '>' || row.comparator === '>=') &&
                    (!row.minValue || row.minValue <= 0)
                  ) {
                    notification.warn({ message: `Min Value should be greater than 0 for ${row.name}` });
                    return;
                  }
                  if (
                    row.comparator === 'BETWEEN' &&
                    (!row.minValue || row.minValue < 0)
                  ) {
                    notification.warn({ message: `Min Value should be greater than or equal to 0 for ${row.name}` });
                    return;
                  }
                  if (
                    row.comparator === 'BETWEEN' &&
                    (!row.minValue || !row.maxValue || row.minValue >= row.maxValue)
                  ) {
                    notification.warn({ message: `Max Value should be greater than Min Value for ${row.name}` });
                    return;
                  }
                  if (row.comparator === '=' && (!row.maxValue || row.maxValue <= 0)) {
                    notification.warn({ message: `Value should be greater than 0 for ${row.name}` });
                    return;
                  }
                }
              }
            }
            const tableDataPayload =
              showExtraFields && dataTable.rows.length > 0
                ? {
                    ...dataTable,
                    mode: 'single',
                    rows: dataTable.rows.map((row) => ({
                      ...row,
                      unit: data.testValue || row.unit,
                      comparator,
                      minValue:
                        data.minValue != null && data.minValue !== ''
                          ? data.minValue
                          : row.minValue ?? '',
                      maxValue:
                        data.maxValue != null && data.maxValue !== ''
                          ? data.maxValue
                          : row.maxValue ?? '',
                    })),
                  }
                : {
                    ...dataTable,
                    mode: showExtraFields ? 'single' : 'multiple',
                  };

            const obj = {
              processDescription: processDescription,
              applicableStandardsCode: applicableStandardsCode,
              testEquipment: testEquipment,
              acceptanceCriteria: acceptanceCriteria,
              tableData: tableDataPayload,
              testId: data.testId,
              noOfSample: null,
              minValue: +data.minValue,
              comparator: comparator,
              maxValue: +data.maxValue,
              mode: showExtraFields ? 'single' : 'multiple',
              typeOfTest: 'Table',
              sowStationId: parseInt(sowStatId),
              testValueTypeId: testTypeValue?.id || null,
              mandatory: data.mandatory,
              imagesRequired: resolvedImages,
              stopwatchValueRequired: resolvedStopwatch,
            };
            if (obj.mode === 'multiple') {
              obj.testValueTypeId = 1;
              obj.comparator = '';
            }
            setLoadingBtn(true);
            if (editTest) {
              await onSaveEditTestDetails(obj, editTestId);
            } else {
              await onSaveTestDetails(obj);
            }

            setLoadingBtn(false);
            setIsTestModalVisible(false);
            setCriteria('');
            setInputValue(false);
            setTestValue('');
            setImagesRequired(false);
            setStopwatchValueRequired(false);
            setComparator('<');
            testForm.resetFields(); //resetting input fields
          } else if (data.testValue !== undefined) {
            if (!validateComparators(data)) {
              return; // Stop submission if validation fails
            }
            setLoadingBtn(true);
            const obj = {
              processDescription: processDescription,
              applicableStandardsCode: applicableStandardsCode,
              testEquipment: testEquipment,
              acceptanceCriteria: acceptanceCriteria,
              testId: data.testId,
              typeOfTest: 'Value',
              tableData: null,
              noOfSample: noOfSample,
              sowStationId: parseInt(sowStatId),
              testValueTypeId: testTypeValue?.id || null,
              minValue: +data.minValue,
              comparator: comparator,
              maxValue: +data.maxValue,
              mandatory: data.mandatory,
              imagesRequired: resolvedImages,
              stopwatchValueRequired: resolvedStopwatch,
            };
            if (editTest) {
              if (data.testValue) {
                await onSaveEditTestDetails(obj, editTestId);

                setIsTestModalVisible(false);
                setCriteria('');
                setInputValue(false);
                setTestValue('');
                setMaxValue('');
                setMinValue('');
                setImagesRequired(false);
                setStopwatchValueRequired(false);
                setComparator('<');
                testForm.resetFields(); //resetting input fields
              } else {
                notification.warning({
                  message: 'Select Value Unit to Proceed!',
                });
              }
            } else {
              await onSaveTestDetails(obj);
              setLoadingBtn(false);
              setIsTestModalVisible(false);
              setCriteria('');
              setInputValue(false);
              setTestValue('');
              setMaxValue('');
              setMinValue('');
              setImagesRequired(false);
              setStopwatchValueRequired(false);
              setComparator('<');
              testForm.resetFields(); //resetting input fields
              setLoadingBtn(true);
            }

            setLoadingBtn(false);
          } else {
            notification.warning({
              message: 'Select Value Unit to Proceed!',
            });
          }
        } else {
          notification.warning({
            message: 'Fill All the required fields to proceed!',
          });
        }
      })
      .catch((error) => {
        console.error('Validation Error:', error);
      });
  };

  /** Function for on select of Criteria */
  const onChangeCriteria = (value) => {
    setCriteria(value);
    if (criteriaList.length > 0) {
      const selected = criteriaList.find((u) => u.value === value);
      if (selected && selected.id === 1) {
        setValueObj(selected.types);

        const testValueTypeId =
          testTypesList && testTypesList?.find((u) => u?.valueType === selected?.valueType);
        setTestTypeValue(testValueTypeId);
        setInputValue(false);
        setIsTableSelected(false);
      } else if (selected && selected.id === 4) {
        setValueObj(selected.types);
        const testValueTypeId =
          testTypesList && testTypesList?.find((u) => u?.valueType === selected?.valueType);
        setTestTypeValue(testValueTypeId);
        setInputValue(false);
        setIsTableSelected(false);
      } else if (selected && selected.id === 3) {
        setValueObj(selected.types);
        setIsTableSelected(true);
        setInputValue(false);
        // } else if () {

      } else {
        setValueObj(selected.types);
        setTestTypeValue('');
        setInputValue(true);
        setIsTableSelected(false);
      }
    }
  };

  /** Function for on select of unit value */
  const onChangeValue = (value) => {
    setTestValue(value);
    if (valueObj.length > 0) {
      const selected = valueObj.find((u) => u.value === value);
      const testValueTypeId =
        testTypesList && testTypesList.find((u) => u.valueType === selected.valueType);
      setTestTypeValue(testValueTypeId);
    }
  };

  const onChangeCompValue = (value) => {
    setComparator(normalizePqtComparator(value));
    if (value === '>' || value === '>=') {
      setMaxValue('0');
      testForm.setFieldsValue({ maxValue: '0' });
    } else if (value === '<' || value === '<=' || value === '=') {
      testForm.setFieldsValue({ minValue: '0' });
      setMinValue('0');
    }
  };

  // Handle row and column count input
  const handleRowColumnChange = () => {
    const rowNum = testForm.getFieldValue('rowCount') || 0;
    const colNum = testForm.getFieldValue('colCount') || 0;
    setRowCount(rowNum);
    setColCount(colNum);

    // // Create empty row names and column names
    // setRows(Array.from({ length: rowNum }, (_, i) => `Row ${i + 1}`));

    // setColumns(Array.from({ length: colNum }, (_, i) => `Column ${i + 1}`));

    // Update state with new row/column structure
    setDataTable(() => {
      return {
        rows: Array.from({ length: rowNum }, (_, i) => ({
          name: `Row ${i + 1}`,
          values: Array.from({ length: colNum }, (_, i) => ''), // Empty values for each row
          unit: '', // Table row unit - starts empty for user input
          comparator: '<', // Table row comparator - starts with default
          minValue: '', // Table row minValue - starts empty for user input
          maxValue: '', // Table row maxValue - starts empty for user input
        })),
        columns: Array.from({ length: colNum }, (_, i) => `Column ${i + 1}`),
      };
    });
  };

  // Update row name while preserving values
  const updateRowName = (index, newName) => {
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], name: newName };
      return { ...prevData, rows: updatedRows };
    });
  };

  // Update column name
  const updateColumnName = (index, newName) => {
    setDataTable((prevData) => {
      const updatedColumns = [...prevData.columns];
      updatedColumns[index] = newName;
      return { ...prevData, columns: updatedColumns };
    });
  };

  // Update comparator for a specific row
  const updateRowComparator = (index, newComparator) => {
    const next = normalizePqtComparator(newComparator);
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], comparator: next };
      return { ...prevData, rows: updatedRows };
    });
  };

  // Update minValue for a specific row
  const updateRowMinValue = (index, newMinValue) => {
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], minValue: newMinValue };
      return { ...prevData, rows: updatedRows };
    });
  };

  // Update maxValue for a specific row
  const updateRowMaxValue = (index, newMaxValue) => {
    setDataTable((prevData) => {
      const updatedRows = [...prevData.rows];
      updatedRows[index] = { ...updatedRows[index], maxValue: newMaxValue };
      return { ...prevData, rows: updatedRows };
    });
  };

  const gettableColumns = () => {
    // Generate Table Columns
    const tableColumns = [
      {
        title: <div className="row-title">Rows / Columns</div>,
        dataIndex: 'rowName',
        key: 'rowName',
        fixed: 'left',
      },
      ...dataTable.columns.map((col, index) => ({
        title: (
          <Input
            className="row-title"
            value={col}
            onChange={(e) => updateColumnName(index, e.target.value)}
            placeholder={`Column ${index + 1}`}
          />
        ),
        dataIndex: `col_${index}`,
        key: `col_${index}`,
        render: (_, row) => (
          <Input
            disabled
            value={row.values[index] || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              setDataTable((prevData) => {
                const updatedRows = [...prevData.rows];
                updatedRows[row.key].values[index] = newValue;
                return { ...prevData, rows: updatedRows };
              });
            }}
          />
        ),
      })),
      // Add Unit Selection Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields ? [{
        title: <div className="row-title">Unit</div>,
        dataIndex: 'unit',
        key: 'unit',
        width: 80,
        render: (_, row) => (
          <PqtFormSelect
            variant="compact"
            placeholder="Unit"
            value={row.unit || undefined}
            onChange={(value) => {
              setDataTable((prevData) => {
                const updatedRows = [...prevData.rows];
                updatedRows[row.key].unit = value;
                return { ...prevData, rows: updatedRows };
              });
            }}
            options={
              valueObj
                ? valueObj
                    .filter(
                      (item) =>
                        item.value !== undefined &&
                        item.value !== '' &&
                        item.value !== 'Accept/Reject/NA' &&
                        item.value !== 'Text'
                    )
                    .map((item) => ({ value: item.value, label: item.value }))
                : []
            }
          />
        ),
      }] : []),
      // Add Comparator Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields ? [{
        title: <div className="row-title">Comparator</div>,
        dataIndex: 'comparator',
        key: 'comparator',
        width: 80,
        render: (_, row) => (
          <PqtFormSelect
            variant="compact"
            placeholder="Comparator"
            value={normalizePqtComparator(row.comparator)}
            onChange={(value) => updateRowComparator(row.key, value)}
            options={comparatorValue.map((item) => ({ value: item, label: item }))}
          />
        ),
      }] : []),
      // Add Min Value Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields && dataTable.rows.some(row =>
        row.comparator === '>' ||
        row.comparator === '>=' ||
        row.comparator === 'BETWEEN'
      ) ? [{
        title: <div className="row-title">Min</div>,
        dataIndex: 'minValue',
        key: 'minValue',
        width: 70,
        render: (_, row) => {
          const shouldShow = row.comparator === '>' ||
            row.comparator === '>=' ||
            row.comparator === 'BETWEEN'; 
          return shouldShow ? (
            <InputNumber
              className="pqt-modal-input-number"
              style={{ width: '100%' }}
              placeholder="Min"
              value={row.minValue}
              onChange={(value) => updateRowMinValue(row.key, value)}
            />
          ) : null;
        },
      }] : []),
      // Add Max Value Column (conditionally rendered - only for Multiple mode)
      ...(!showExtraFields && dataTable.rows.some(row =>
        row.comparator === '<' ||
        row.comparator === '<=' ||
        row.comparator === '=' ||
        row.comparator === 'BETWEEN'
      ) ? [{
        title: <div className="row-title">
          {dataTable.rows.some(row => row.comparator === '=') ? 'Value' : 'Max'}
        </div>,
        dataIndex: 'maxValue',
        key: 'maxValue',
        width: 70,
        render: (_, row) => {
          const shouldShow = row.comparator === '<' ||
            row.comparator === '<=' ||
            row.comparator === '=' ||
            row.comparator === 'BETWEEN';
          return shouldShow ? (
            <InputNumber
              style={{ width: '100%' }}
              placeholder={row.comparator === '=' ? 'Value' : 'Max'}
              value={row.maxValue}
              onChange={(value) => updateRowMaxValue(row.key, value)}
              min={row.comparator === 'BETWEEN' ? (row.minValue || 0) : undefined}
            />
          ) : null;
        },
      }] : []),
    ];
    return tableColumns;
  };
  const getTableData = () => {
    // Generate Table Data
    const tableData = dataTable.rows.map((row, rowIndex) => ({
      key: rowIndex,
      rowName: (
        <Input
          value={row.name}
          onChange={(e) => updateRowName(rowIndex, e.target.value)}
          placeholder={`Row ${rowIndex + 1}`}
        />
      ),
      values: row.values,
      unit: row.unit, // Include unit data
      comparator: row.comparator, // Include comparator data
      minValue: row.minValue, // Include minValue data
      maxValue: row.maxValue, // Include maxValue data
    }));
    return tableData;
  };

  return (
    <>
      <div className="new-test">
        <CrceButton
          label="Add test"
          variant="tertiary"
          size="small"
          icon={Plus}
          onClick={showModal}
        />
      </div>

      {isTestModalVisible && (
        <div
          className="mc-dpr-modal-backdrop mc-dpr-modal-backdrop--add-test"
          role="presentation"
          onClick={() => !loadingBtn && handleCancel()}
        >
          <div
            className="mc-dpr-modal mc-dpr-modal--large add-new-test-mc-modal"
            role="dialog"
            aria-labelledby="pqt-add-test-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="pqt-add-test-modal-title" className="mc-dpr-modal-title">
                {editTest ? 'Edit test' : 'Add test'}
              </h3>
              <button
                type="button"
                className="mc-dpr-modal-close"
                aria-label="Close"
                disabled={loadingBtn}
                onClick={handleCancel}
              >
                <X size={20} strokeWidth={2} aria-hidden />
              </button>
            </div>
            <div className="mc-dpr-modal-body">
          <Form form={testForm} name="basic" layout="vertical" autoComplete="off">
            <Row gutter={16}>
              <Col lg={12}>
                <Form.Item label="Process Description" name="processDescription" required>
                  <TextArea
                    className="pqt-modal-textarea"
                    value={processDescription}
                    size="large"
                    onChange={(e) => setProcessDescription(e.target.value)}
                    style={{
                      height: 60, // Fixed height
                      overflowY: 'auto', // Enables scrollbar when needed
                      resize: 'none', // Prevents manual resizing
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item
                  label="Applicable Standards & Code"
                  name="applicableStandardsCode"
                  required
                >
                  <TextArea
                    className="pqt-modal-textarea"
                    value={applicableStandardsCode}
                    size="large"
                    onChange={(e) => setApplicableStandardsCode(e.target.value)}
                    style={{
                      height: 60, // Fixed height
                      overflowY: 'auto', // Enables scrollbar when needed
                      resize: 'none', // Prevents manual resizing
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item label="Test Equipment" name="testEquipment" required>
                  <TextArea
                    className="pqt-modal-textarea"
                    value={testEquipment}
                    size="large"
                    onChange={(e) => setTestEquipment(e.target.value)}
                    style={{
                      height: 60, // Fixed height
                      overflowY: 'auto', // Enables scrollbar when needed
                      resize: 'none', // Prevents manual resizing
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item label="Acceptance Criteria" name="acceptanceCriteria" required>
                  <TextArea
                    className="pqt-modal-textarea"
                    value={acceptanceCriteria}
                    size="large"
                    onChange={(e) => setAcceptanceCriteria(e.target.value)}
                    style={{
                      height: 60, // Fixed height
                      overflowY: 'auto', // Enables scrollbar when needed
                      resize: 'none', // Prevents manual resizing
                    }}
                  />
                </Form.Item>
              </Col>
              <Col lg={6}>
                <Form.Item
                  label="Test ID"
                  name="testId"
                  size="large"
                  required
                  rules={[
                    {
                      required: true,
                      message: 'Please enter Test ID',
                    },
                    {
                      max: 50,
                      message: 'Test ID length must be less than or equal to 50 characters',
                    },
                  ]}
                >
                  <Input
                    value={testId}
                    size="large"
                    onChange={(e) => setTestId(e.target.value)}
                    placeholder="Enter Test ID"
                  />
                </Form.Item>
              </Col>

              <Col lg={6}>
                <Form.Item label="Criteria" name="criteria" required>
                  <PqtFormSelect
                    className="client-select"
                    placeholder="Select Criteria"
                    onChange={onChangeCriteria}
                    value={criteria}
                    options={(criteriaList || [])
                      .filter((item) => item && item.value != null && item.value !== '')
                      .map((item) => ({ value: item.value, label: item.value }))}
                  />
                </Form.Item>
              </Col>
              {isTableSelected && (
                <>
                  <Col span={6}>
                    <Form.Item label="No of Rows" name="rowCount">
                      <Input type="number" min={1} onChange={handleRowColumnChange} size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="No of Columns" name="colCount">
                      <Input type="number" min={1} onChange={handleRowColumnChange} size="large" />
                    </Form.Item>
                  </Col>

                <Col span={24}>
                    <Form.Item
                      label="Unit / Comparator Mode:"
                      name="extraFieldsMode"
                      className="report-radio add-new-test-comparator-mode"
                    >
                    <Radio.Group
                      value={showExtraFields}
                        onChange={(e) => {
                        setShowExtraFields(e.target.value);
                        testForm.setFieldsValue({ extraFieldsMode: e.target.value });
                      }}
                      className="add-new-test-comparator-radio-group"
                    >
                      <Radio value={true}>Single (per table)</Radio>
                      <Radio value={false}>Multiple (per row)</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                </>
              )}
              {inputValue && (
                <Col lg={6}>
                  <Form.Item label="Number of sample" name="noOfSample" required>
                    <Input
                      value={noOfSample}
                      onChange={(e) => setNoOfSample(e.target.value)}
                      size="large"
                      type="number"
                      min={1}
                      defaultValue={1}
                    />
                  </Form.Item>
                </Col>
              )}
              {(inputValue || (isTableSelected && showExtraFields === true)) && (
                <>
                  <Col lg={6}>
                    <Form.Item label="Unit" name="testValue" required>
                      <PqtFormSelect
                        className="client-select"
                        placeholder="Select Unit"
                        onChange={onChangeValue}
                        value={testValue}
                        options={
                          valueObj
                            ? valueObj
                                .filter(
                                  (item) =>
                                    item.value !== undefined &&
                                    item.value !== '' &&
                                    item.value !== 'Accept/Reject/NA'
                                    && item.value !== 'Text'
                                )
                                .map((item) => ({ value: item.value, label: item.value }))
                            : []
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col lg={6}>
                    <Form.Item label="Comparator" name="comparator" required>
                      <PqtFormSelect
                        className="client-select"
                        placeholder="Select Comparator"
                        onChange={onChangeCompValue}
                        value={normalizePqtComparator(comparator)}
                        options={comparatorValue.map((item) => ({ value: item, label: item }))}
                      />
                    </Form.Item>
                  </Col>
                  {comparator !== 'None' && (
                    <>
                      {(comparator == '>' || comparator == '>=' || comparator == 'BETWEEN') && (
                        <Col lg={6} style={{ minWidth: 168 }}>
                          <Form.Item label="Min. Value" name="minValue" required>
                            <InputNumber
                              className="pqt-modal-input-number"
                              style={{ width: '100%' }}
                              value={minValue}
                              onChange={(value) => setMinValue(value)}
                              size="large"
                              disabled={
                                comparator == '<' || comparator == '<=' || comparator == '='
                              }
                            />
                          </Form.Item>
                        </Col>
                      )}

                      {(comparator == '<' ||
                        comparator == '<=' ||
                        comparator == '=' ||
                        comparator == 'BETWEEN') && (
                        <Col lg={6} style={{ minWidth: 168 }}>
                          <Form.Item
                            label={comparator == '=' ? 'Value' : 'Max. Value'}
                            name="maxValue"
                            required
                          >
                            <InputNumber
                              className="pqt-modal-input-number"
                              style={{ width: '100%' }}
                              value={maxValue}
                              min={
                                comparator == 'BETWEEN' ? parseInt(String(minValue), 10) || 0 : undefined
                              }
                              onChange={(value) => {
                                setMaxValue(value);
                              }}
                              size="large"
                              disabled={comparator == '>' || comparator == '>='}
                            />
                          </Form.Item>
                        </Col>
                      )}
                    </>
                  )}
                </>
              )}
            </Row>
            {isTableSelected && rowCount > 0 && colCount > 0 && (
              <Table
                columns={gettableColumns()}
                dataSource={getTableData()}
                bordered
                pagination={false}
                className="itp-container"
              />
            )}
            {/* </Form> */}
            <div className="pqt-modal-options-stack">
              <Form.Item
                name="mandatory"
                className="pqt-modal-form-option"
                getValueFromEvent={(v) => v}
                rules={[{ required: true, message: 'Choose mandatory or optional' }]}
              >
                <PqtModalMandatoryRow />
              </Form.Item>
              <Form.Item
                name="imagesRequired"
                className="pqt-modal-form-option"
                getValueFromEvent={(v) => v}
              >
                <PqtModalToggleRow
                  label="Images"
                  RowIcon={Image}
                  syncParent={setImagesRequired}
                  hintTrue="* For this test, image is required."
                  hintFalse="* For this test, image is not required."
                />
              </Form.Item>
              <Form.Item
                name="stopwatchValueRequired"
                className="pqt-modal-form-option"
                getValueFromEvent={(v) => v}
              >
                <PqtModalToggleRow
                  label="Stopwatch"
                  RowIcon={Timer}
                  syncParent={setStopwatchValueRequired}
                  hintTrue="* For this test, Stopwatch is required."
                  hintFalse="* For this test, Stopwatch is not required."
                />
              </Form.Item>
            </div>
          </Form>
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton
                label="Cancel"
                variant="tertiary"
                size="medium"
                onClick={handleCancel}
                disabled={loadingBtn}
              />
              <CrceButton
                label={loadingBtn ? (editTest ? 'Updating…' : 'Adding…') : editTest ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleOk}
                disabled={loadingBtn}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddNewPQTTest;
